import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Disputes API
export const disputesApi = {
  getAll: async () => {
    const response = await axios.get(`${API}/disputes`, { headers: getAuthHeader() });
    return response.data;
  },
  getById: async (id) => {
    const response = await axios.get(`${API}/disputes/${id}`, { headers: getAuthHeader() });
    return response.data;
  },
  analyze: async () => {
    const response = await axios.post(`${API}/disputes/analyze`, {}, { headers: getAuthHeader() });
    return response.data;
  },
  generate: async () => {
    const response = await axios.post(`${API}/disputes/generate`, {}, { headers: getAuthHeader() });
    return response.data;
  },
  addCreditItems: async (items) => {
    const response = await axios.post(`${API}/disputes/credit-items`, items, { headers: getAuthHeader() });
    return response.data;
  },
  getCreditItems: async () => {
    const response = await axios.get(`${API}/disputes/credit-items/all`, { headers: getAuthHeader() });
    return response.data;
  },
  updateStatus: async (id, status, resolution) => {
    const response = await axios.put(`${API}/disputes/${id}/status`, null, {
      headers: getAuthHeader(),
      params: { status, resolution }
    });
    return response.data;
  },
  // PDF Downloads
  downloadSummaryPdf: async () => {
    const response = await axios.get(`${API}/disputes/pdf/summary`, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    return response.data;
  },
  downloadLetterPdf: async (disputeId) => {
    const response = await axios.get(`${API}/disputes/pdf/letter/${disputeId}`, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    return response.data;
  },
  // Instant Recommendations
  getInstantRecommendations: async () => {
    const response = await axios.post(`${API}/disputes/instant-recommendations`, {}, { headers: getAuthHeader() });
    return response.data;
  },
  // Credit Score Tracking
  addCreditScore: async (score, bureau = 'Experian', notes = null) => {
    const response = await axios.post(`${API}/disputes/credit-score`, { score, bureau, notes }, { headers: getAuthHeader() });
    return response.data;
  },
  getCreditScoreHistory: async () => {
    const response = await axios.get(`${API}/disputes/credit-score/history`, { headers: getAuthHeader() });
    return response.data;
  },
  getLatestCreditScore: async () => {
    const response = await axios.get(`${API}/disputes/credit-score/latest`, { headers: getAuthHeader() });
    return response.data;
  },
  deleteCreditScore: async (scoreId) => {
    const response = await axios.delete(`${API}/disputes/credit-score/${scoreId}`, { headers: getAuthHeader() });
    return response.data;
  },
  // Score Goal
  setScoreGoal: async (targetScore, targetDate = null, notes = null) => {
    const response = await axios.post(`${API}/disputes/score-goal`, { 
      target_score: targetScore, 
      target_date: targetDate,
      notes 
    }, { headers: getAuthHeader() });
    return response.data;
  },
  getScoreGoal: async () => {
    const response = await axios.get(`${API}/disputes/score-goal`, { headers: getAuthHeader() });
    return response.data;
  },
  deleteScoreGoal: async () => {
    const response = await axios.delete(`${API}/disputes/score-goal`, { headers: getAuthHeader() });
    return response.data;
  }
};

// AI Coach API
export const coachApi = {
  chat: async (message) => {
    const response = await axios.post(`${API}/coach/chat`, { message }, { headers: getAuthHeader() });
    return response.data;
  },
  getInsights: async () => {
    const response = await axios.get(`${API}/coach/insights`, { headers: getAuthHeader() });
    return response.data;
  },
  getActionPlan: async () => {
    const response = await axios.get(`${API}/coach/action-plan`, { headers: getAuthHeader() });
    return response.data;
  },
  getHistory: async () => {
    const response = await axios.get(`${API}/coach/history`, { headers: getAuthHeader() });
    return response.data;
  }
};

// Documents API
export const documentsApi = {
  getAll: async () => {
    const response = await axios.get(`${API}/documents`, { headers: getAuthHeader() });
    return response.data;
  },
  upload: async (document) => {
    const response = await axios.post(`${API}/documents/upload`, document, { headers: getAuthHeader() });
    return response.data;
  },
  delete: async (id) => {
    const response = await axios.delete(`${API}/documents/${id}`, { headers: getAuthHeader() });
    return response.data;
  }
};

// Payments API
export const paymentsApi = {
  getPlans: async () => {
    const response = await axios.get(`${API}/payments/plans`);
    return response.data;
  },
  createCheckout: async (plan, originUrl) => {
    const response = await axios.post(`${API}/payments/checkout`, 
      { plan, origin_url: originUrl },
      { headers: getAuthHeader() }
    );
    return response.data;
  },
  getStatus: async (sessionId) => {
    const response = await axios.get(`${API}/payments/status/${sessionId}`, { headers: getAuthHeader() });
    return response.data;
  }
};

// Notifications API
export const notificationsApi = {
  getAll: async () => {
    const response = await axios.get(`${API}/notifications`, { headers: getAuthHeader() });
    return response.data;
  },
  markRead: async (id) => {
    const response = await axios.put(`${API}/notifications/${id}/read`, {}, { headers: getAuthHeader() });
    return response.data;
  }
};

// Admin API
export const adminApi = {
  getUsers: async () => {
    const response = await axios.get(`${API}/admin/users`, { headers: getAuthHeader() });
    return response.data;
  },
  getUserDetails: async (userId) => {
    const response = await axios.get(`${API}/admin/users/${userId}`, { headers: getAuthHeader() });
    return response.data;
  },
  getTemplates: async () => {
    const response = await axios.get(`${API}/admin/templates`, { headers: getAuthHeader() });
    return response.data;
  },
  createTemplate: async (template) => {
    const response = await axios.post(`${API}/admin/templates`, template, { headers: getAuthHeader() });
    return response.data;
  },
  updateTemplate: async (id, template) => {
    const response = await axios.put(`${API}/admin/templates/${id}`, template, { headers: getAuthHeader() });
    return response.data;
  },
  getAnalytics: async () => {
    const response = await axios.get(`${API}/admin/analytics`, { headers: getAuthHeader() });
    return response.data;
  },
  getAuditLogs: async () => {
    const response = await axios.get(`${API}/admin/audit-logs`, { headers: getAuthHeader() });
    return response.data;
  },
  updateUserRole: async (userId, role) => {
    const response = await axios.put(`${API}/admin/users/${userId}/role`, null, {
      headers: getAuthHeader(),
      params: { role }
    });
    return response.data;
  },
  // Articles
  getArticles: async () => {
    const response = await axios.get(`${API}/admin/articles`, { headers: getAuthHeader() });
    return response.data;
  },
  createArticle: async (article) => {
    const response = await axios.post(`${API}/admin/articles`, article, { headers: getAuthHeader() });
    return response.data;
  },
  updateArticle: async (id, article) => {
    const response = await axios.put(`${API}/admin/articles/${id}`, article, { headers: getAuthHeader() });
    return response.data;
  },
  deleteArticle: async (id) => {
    const response = await axios.delete(`${API}/admin/articles/${id}`, { headers: getAuthHeader() });
    return response.data;
  },
  // Settings
  getSettings: async () => {
    const response = await axios.get(`${API}/admin/settings`, { headers: getAuthHeader() });
    return response.data;
  },
  updateSettings: async (settings) => {
    const response = await axios.put(`${API}/admin/settings`, settings, { headers: getAuthHeader() });
    return response.data;
  }
};

// Articles API (public)
export const articlesApi = {
  getAll: async (category) => {
    const params = category ? { category } : {};
    const response = await axios.get(`${API}/articles`, { params });
    return response.data;
  },
  getBySlug: async (slug) => {
    const response = await axios.get(`${API}/articles/${slug}`);
    return response.data;
  },
  getCategories: async () => {
    const response = await axios.get(`${API}/articles/categories/list`);
    return response.data;
  }
};

// Marketplace API
export const marketplaceApi = {
  // Credit Builder Marketplace
  getCreditProviders: async () => {
    const response = await axios.get(`${API}/marketplace/credit-builders`, { headers: getAuthHeader() });
    return response.data;
  },
  getCreditCategories: async () => {
    const response = await axios.get(`${API}/marketplace/credit-builders/categories`);
    return response.data;
  },
  trackCreditClick: async (providerId) => {
    const response = await axios.post(`${API}/marketplace/credit-builders/${providerId}/click`, {}, { headers: getAuthHeader() });
    return response.data;
  },
  
  // Financial Growth Marketplace
  getFinancialProviders: async () => {
    const response = await axios.get(`${API}/marketplace/financial-growth`, { headers: getAuthHeader() });
    return response.data;
  },
  getFinancialCategories: async () => {
    const response = await axios.get(`${API}/marketplace/financial-growth/categories`);
    return response.data;
  },
  trackFinancialClick: async (providerId) => {
    const response = await axios.post(`${API}/marketplace/financial-growth/${providerId}/click`, {}, { headers: getAuthHeader() });
    return response.data;
  },
  
  // Admin - Credit Providers
  adminGetCreditProviders: async () => {
    const response = await axios.get(`${API}/marketplace/admin/credit-builders`, { headers: getAuthHeader() });
    return response.data;
  },
  adminCreateCreditProvider: async (provider) => {
    const response = await axios.post(`${API}/marketplace/admin/credit-builders`, provider, { headers: getAuthHeader() });
    return response.data;
  },
  adminUpdateCreditProvider: async (providerId, provider) => {
    const response = await axios.put(`${API}/marketplace/admin/credit-builders/${providerId}`, provider, { headers: getAuthHeader() });
    return response.data;
  },
  adminDeleteCreditProvider: async (providerId) => {
    const response = await axios.delete(`${API}/marketplace/admin/credit-builders/${providerId}`, { headers: getAuthHeader() });
    return response.data;
  },
  adminReorderCreditProvider: async (providerId, newOrder) => {
    const response = await axios.put(`${API}/marketplace/admin/credit-builders/${providerId}/reorder`, null, { 
      headers: getAuthHeader(),
      params: { new_order: newOrder }
    });
    return response.data;
  },
  
  // Admin - Financial Providers
  adminGetFinancialProviders: async () => {
    const response = await axios.get(`${API}/marketplace/admin/financial-growth`, { headers: getAuthHeader() });
    return response.data;
  },
  adminCreateFinancialProvider: async (provider) => {
    const response = await axios.post(`${API}/marketplace/admin/financial-growth`, provider, { headers: getAuthHeader() });
    return response.data;
  },
  adminUpdateFinancialProvider: async (providerId, provider) => {
    const response = await axios.put(`${API}/marketplace/admin/financial-growth/${providerId}`, provider, { headers: getAuthHeader() });
    return response.data;
  },
  adminDeleteFinancialProvider: async (providerId) => {
    const response = await axios.delete(`${API}/marketplace/admin/financial-growth/${providerId}`, { headers: getAuthHeader() });
    return response.data;
  },
  adminReorderFinancialProvider: async (providerId, newOrder) => {
    const response = await axios.put(`${API}/marketplace/admin/financial-growth/${providerId}/reorder`, null, { 
      headers: getAuthHeader(),
      params: { new_order: newOrder }
    });
    return response.data;
  },
  
  // Analytics
  getAnalytics: async () => {
    const response = await axios.get(`${API}/marketplace/admin/analytics`, { headers: getAuthHeader() });
    return response.data;
  }
};
